import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FaEnvelope, FaLock, FaCheck, FaTimes } from "react-icons/fa";
import WebsiteNavbar from "../components/WebsiteNavbar";
import { forgotPassword, verifyOtp, resetPassword } from "../services/authService";
import { checkPasswordComplexity, PASSWORD_REQUIREMENTS } from "../utils/passwordValidator";
import "../style/auth.css";
import loginImage from "../images/loginImage.png";

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [otpDigits, setOtpDigits] = useState(["", "", "", "", "", ""]);
  const [resetToken, setResetToken] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const otpRefs = useRef([]);
  const [resending, setResending] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  useEffect(() => { setError(""); }, [step]);

  useEffect(() => {
    let interval = null;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [resendTimer]);

  const handleSendCode = async () => {
    setError("");
    if (!email) { setError("Please enter your email."); return; }
    setLoading(true);
    const result = await forgotPassword(email);
    setLoading(false);
    if (!result.success) { setError(result.error || "Failed to send code."); return; }
    setStep(2);
    setResendTimer(60);
  };

  const handleOtpChange = (index, value) => {
    if (value && !/^[0-9]$/.test(value)) return;
    const updated = [...otpDigits];
    updated[index] = value;
    setOtpDigits(updated);
    if (value && index < 5) otpRefs.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otpDigits[index] && index > 0)
      otpRefs.current[index - 1]?.focus();
  };

  const handleOtpPaste = (e) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!pasted) return;
    e.preventDefault();
    const updated = pasted.split("");
    while (updated.length < 6) updated.push("");
    setOtpDigits(updated);
    otpRefs.current[Math.min(pasted.length, 5)]?.focus();
  };

  const handleVerifyOtp = async () => {
    setError("");
    const otp = otpDigits.join("");
    if (otp.length !== 6) { setError("Please enter all 6 digits."); return; }
    setLoading(true);
    const result = await verifyOtp(email, otp);
    setLoading(false);
    if (!result.success || !result.resetToken) {
      setError(result.error || "Invalid OTP.");
      return;
    }
    setResetToken(result.resetToken);
    setStep(3);
  };

  const handleResend = async () => {
    if (resendTimer > 0) return;
    setError("");
    setOtpDigits(["", "", "", "", "", ""]);
    setResending(true);
    const result = await forgotPassword(email);
    setResending(false);
    if (result.success) {
      setResendTimer(60);
    } else {
      setError(result.error || "Failed to resend code.");
    }
  };

  const pwValidation = checkPasswordComplexity(password);
  const passwordsMatch = password.length > 0 && password === confirmPassword;

  const handleResetPassword = async () => {
    setError("");
    if (!password) { setError("Please enter a password."); return; }
    if (!pwValidation.isValid) { setError(pwValidation.error); return; }
    if (password !== confirmPassword) { setError("Passwords do not match."); return; }
    if (!resetToken) { setError("Verification token missing. Please verify your code again."); return; }
    setLoading(true);
    const result = await resetPassword(email, password, resetToken);
    setLoading(false);
    if (!result.success) { setError(result.error || "Failed to reset password."); return; }
    setStep(4);
  };

  const stepTitles = {
    1: { title: "Forgot", accent: "Password?", sub: "Enter your email and we'll send you a 6-digit reset code." },
    2: { title: "Check your", accent: "Email", sub: `We sent a 6-digit code to ${email}` },
    3: { title: "New", accent: "Password", sub: "Choose a strong password for your account." },
    4: { title: "All", accent: "Done!", sub: "Your password has been reset. You can now sign in with your new password." },
  };

  const { title, accent, sub } = stepTitles[step];
  const preventPasteShortcut = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "v") {
      e.preventDefault();
    }
  };
  return (
    <>
      <WebsiteNavbar />
      <div className="auth-page">
        <div className="auth-card">
          <div className="auth-left">
            {step < 4 && (
              <div className="fp-dots">
                {[1, 2, 3].map((s) => (
                  <span
                    key={s}
                    className={`fp-dot ${step >= s ? "active" : ""}`}
                  />
                ))}
              </div>
            )}

            <h1 className="auth-title">
              {title} <span className="auth-accent">{accent}</span>
            </h1>

            <p className="auth-subtitle">{sub}</p>

            {error && <div className="auth-error">{error}</div>}

            {step === 1 && (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSendCode();
                }}
              >
                <div className="auth-field">
                  <FaEnvelope className="auth-field-icon" />
                  <input
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onPaste={(e) => e.preventDefault()}
                    onDrop={(e) => e.preventDefault()}
                    onKeyDown={preventPasteShortcut}
                    className="auth-input"
                    required
                  />
                </div>
                <button
                  type="submit"
                  className="auth-submit-btn"
                  disabled={loading}
                >
                  {loading ? "Sending..." : "Send Reset Code"}
                </button>
              </form>
            )}

            {step === 2 && (
              <>
                <div className="fp-otp-row" onPaste={handleOtpPaste}>
                  {otpDigits.map((digit, i) => (
                    <input
                      key={i}
                      ref={(el) => (otpRefs.current[i] = el)}
                      className="fp-otp-input"
                      type="text"
                      maxLength={1}
                      inputMode="numeric"
                      value={digit}
                      onChange={(e) => handleOtpChange(i, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(i, e)}
                    />
                  ))}
                </div>
                <button
                  className="auth-submit-btn"
                  onClick={handleVerifyOtp}
                  disabled={loading}
                >
                  {loading ? "Verifying..." : "Verify Code"}
                </button>
                <p className="auth-footer-text">
                  Didn't receive it?{" "}
                  <button
                    type="button"
                    className="fp-link"
                    onClick={handleResend}
                    disabled={resending || resendTimer > 0}
                  >
                    {resending
                      ? "Sending..."
                      : resendTimer > 0
                      ? `Resend in ${resendTimer}s`
                      : "Resend code"}
                  </button>
                </p>
              </>
            )}

            {step === 3 && (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleResetPassword();
                }}
              >
                <div className="auth-field">
                  <FaLock className="auth-field-icon" />
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="New password (min. 8 chars)"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="auth-input"
                    required
                  />
                  <button
                    type="button"
                    className="input-icon-btn"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? "🙈" : "👁️"}
                  </button>
                </div>
                <div className="auth-field">
                  <FaLock className="auth-field-icon" />
                  <input
                    type={showConfirm ? "text" : "password"}
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="auth-input"
                    required
                  />
                  <button
                    type="button"
                    className="input-icon-btn"
                    onClick={() => setShowConfirm(!showConfirm)}
                  >
                    {showConfirm ? "🙈" : "👁️"}
                  </button>
                </div>

                {password.length > 0 && (
                  <div className="auth-pw-checklist">
                    {PASSWORD_REQUIREMENTS.map((r) => (
                      <div
                        key={r.key}
                        className={`auth-pw-req ${pwValidation.checks[r.key] ? "met" : "unmet"}`}
                      >
                        {pwValidation.checks[r.key] ? <FaCheck size={11} /> : <FaTimes size={11} />}
                        <span>{r.label}</span>
                      </div>
                    ))}
                    {confirmPassword.length > 0 && (
                      <div
                        className={`auth-pw-req ${passwordsMatch ? "met" : "unmet"}`}
                      >
                        {passwordsMatch ? <FaCheck size={11} /> : <FaTimes size={11} />}
                        <span>Passwords match</span>
                      </div>
                    )}
                  </div>
                )}

                <button
                  type="submit"
                  className="auth-submit-btn"
                  disabled={loading}
                >
                  {loading ? "Updating..." : "Reset Password"}
                </button>
              </form>
            )}

            {step === 4 && (
              <button
                className="auth-submit-btn"
                onClick={() => navigate("/loginpage")}
              >
                Back to Login
              </button>
            )}

            {step === 1 && (
              <p className="auth-footer-text">
                Remember it?{" "}
                <button
                  type="button"
                  className="fp-link"
                  onClick={() => navigate("/loginpage")}
                >
                  Back to login
                </button>
              </p>
            )}
          </div>

          <div className="auth-right">
            <div className="auth-illustration">
              <img src={loginImage} alt="Illustration" className="auth-image" />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}